import React, { useEffect, useRef, useState } from 'react';
import './ChatNotifications.css';

const API = 'https://cms.amservices.eu/api/private_chat.php';

async function chatRequest(path, body) {
  const token = localStorage.getItem('am_token') || JSON.parse(localStorage.getItem('am_user') || 'null')?.token || '';
  const response = await fetch(`${API}${path}`, {
    method: body ? 'POST' : 'GET',
    cache: 'no-store',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok || !data.success) throw new Error(data.message || 'Messagerie indisponible.');
  return data;
}

function FloatingConversation({ contact, userId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    let active = true;
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await chatRequest(`?action=messages&user_id=${contact.id}`);
        if (active) {
          setMessages(current => {
            const latestServerId = Number(data.messages.at(-1)?.id || 0);
            const next = [...data.messages, ...current.filter(item => Number(item.id) > latestServerId)];
            return current.length === next.length && current.every((item, index) => String(item.id) === String(next[index].id)) ? current : next;
          });
          setError('');
        }
      } catch (err) { if (active) setError(err.message); }
      finally { inFlight = false; }
    };
    load();
    const timer = window.setInterval(load, 1000);
    return () => { active = false; window.clearInterval(timer); };
  }, [contact.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  const send = async event => {
    event.preventDefault();
    const message = draft.trim();
    if (!message || sending) return;
    setSending(true);
    setError('');
    try {
      const data = await chatRequest('', { recipient_id: Number(contact.id), message });
      setDraft('');
      setMessages(current => current.some(item => String(item.id) === String(data.id)) ? current : [...current, {
        id: data.id,
        sender_id: userId,
        recipient_id: Number(contact.id),
        message,
        created_at: new Date().toISOString(),
      }]);
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  };

  return <section className="floating-conversation" role="dialog" aria-label={`Conversation avec ${contact.name}`}>
    <header className="floating-conversation-header"><span className="chat-notification-avatar" aria-hidden="true">{contact.name.slice(0, 1).toUpperCase()}</span><strong>{contact.name}</strong><button type="button" onClick={onClose} aria-label="Fermer la conversation">×</button></header>
    {error && <p className="floating-conversation-error" role="alert">{error}</p>}
    <div className="floating-conversation-messages" aria-live="polite">
      {!messages.length && <p className="floating-conversation-empty">Aucun message pour le moment.</p>}
      {messages.map(item => <div key={item.id} className={`floating-conversation-message ${Number(item.sender_id) === userId ? 'mine' : ''}`}><p>{item.message}</p><time>{new Date(item.created_at.replace(' ', 'T')).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</time></div>)}
      <div ref={endRef} />
    </div>
    <form className="floating-conversation-compose" onSubmit={send}><label className="sr-only" htmlFor="floating-chat-draft">Votre message</label><textarea id="floating-chat-draft" rows="2" maxLength="4000" placeholder="Écrire un message…" value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form.requestSubmit(); } }} /><button type="submit" disabled={!draft.trim() || sending}>{sending ? 'Envoi…' : 'Envoyer'}</button></form>
  </section>;
}

export default function ChatNotifications({ user }) {
  const [notices, setNotices] = useState([]);
  const [conversation, setConversation] = useState(null);
  const userId = Number(user?.id);

  useEffect(() => {
    if (!userId) return undefined;
    const storageKey = `ams_private_chat_notified_${userId}`;
    let cursor = localStorage.getItem(storageKey);
    let active = true;
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      const token = localStorage.getItem('am_token') || user?.token || '';
      if (!token) return;
      inFlight = true;
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved !== null && (cursor === null || Number(saved) > Number(cursor))) cursor = saved;
        const query = cursor === null ? '' : `&after=${encodeURIComponent(cursor)}`;
        const response = await fetch(`${API}?action=notifications${query}`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        const data = await response.json();
        if (!response.ok || !data.success || !active) return;
        if (cursor === null) {
          cursor = String(data.last_id || 0);
          localStorage.setItem(storageKey, cursor);
          return;
        }
        const incoming = data.messages || [];
        if (!incoming.length) return;
        cursor = String(incoming[incoming.length - 1].id);
        localStorage.setItem(storageKey, cursor);
        setNotices(current => [...current, ...incoming.map(item => ({ ...item, shownAt: Date.now() }))].slice(-3));
      } catch { /* Le prochain passage réessaiera. */ }
      finally { inFlight = false; }
    };

    poll();
    const pollTimer = window.setInterval(poll, 1000);
    return () => { active = false; window.clearInterval(pollTimer); };
  }, [userId, user?.token]);

  useEffect(() => {
    if (!notices.length) return undefined;
    const timer = window.setTimeout(() => {
      setNotices(current => current.filter(item => Date.now() - item.shownAt < 8000));
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [notices]);

  if (!notices.length && !conversation) return null;
  return <>
    {conversation && <FloatingConversation key={conversation.id} contact={conversation} userId={userId} onClose={() => setConversation(null)} />}
    <div className={`chat-notifications ${conversation ? 'with-panel' : ''}`} aria-live="polite" aria-label="Nouveaux messages privés">
    {notices.map(item => <div className="chat-notification" key={item.id}>
      <button className="chat-notification-open" type="button" onClick={() => { setConversation({ id: Number(item.sender_id), name: item.sender_name || 'Contact' }); setNotices(current => current.filter(notice => notice.id !== item.id)); }}>
        <span className="chat-notification-avatar" aria-hidden="true">{item.sender_name?.slice(0, 1).toUpperCase() || '✉'}</span>
        <span className="chat-notification-content"><strong>{item.sender_name || 'Nouveau message'}</strong><small>{item.message}</small></span>
      </button>
      <button className="chat-notification-close" type="button" aria-label={`Fermer la notification de ${item.sender_name || 'message'}`} onClick={() => setNotices(current => current.filter(notice => notice.id !== item.id))}>×</button>
    </div>)}
    </div>
  </>;
}
